// Socket.IO client for video-export-v2.
// One connection per popup mount; auto-reconnects via socket.io-client's
// built-in retry. The server keeps psubscribe alive on Redis so a brief
// disconnect doesn't lose events — the cold-start GET /:jobId snapshot
// covers any miss-window on reconnect.
//
// websocket-only on purpose: mw runs as a PM2 cluster without sticky
// sessions, so HTTP long-polling would land its requests on different
// instances. The flip side is that networks which block WebSocket (some
// corporate proxies) never connect at all — connectVideoExportSocket
// therefore rejects after a timeout instead of waiting forever, and the
// caller falls back to polling the snapshot.

import { io } from "socket.io-client";
import { getToken } from "../../common/environment_config";

const apiBase = () => (window.ideaLibraryRootUrl || "").replace(/\/$/, "");

const CONNECT_TIMEOUT_MS = 8000;

let _shared = null;

function getSocket() {
  // `active` stays true while socket.io is still (re)connecting. Reuse it
  // then — building a new socket on every popup open left the previous one
  // retrying in the background forever wherever WebSocket is blocked.
  if (_shared && (_shared.connected || _shared.active)) return _shared;
  if (_shared) _shared.close();
  _shared = io(`${apiBase()}/video-export-v2`, {
    transports: ["websocket"],
    // Function form: evaluated on every (re)connect, so a token renewed
    // mid-session is picked up instead of reconnecting with an expired one.
    auth: (cb) => cb({ token: "Bearer " + getToken() }),
    reconnection: true,
    reconnectionDelay: 500,
    reconnectionDelayMax: 5000,
  });
  return _shared;
}

function waitForConnect(socket) {
  return new Promise((resolve, reject) => {
    if (socket.connected) return resolve();
    const onConnect = () => {
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(() => {
      socket.off("connect", onConnect);
      reject(new Error("video-export socket connect timeout"));
    }, CONNECT_TIMEOUT_MS);
    socket.once("connect", onConnect);
  });
}

function joinRoom(socket, jobId) {
  return new Promise((resolve, reject) => {
    socket.timeout(CONNECT_TIMEOUT_MS).emit("video-export:join", { jobId }, (err, ack) => {
      if (!err && ack && ack.ok) resolve();
      else reject(new Error(err ? "video-export:join timeout" : "video-export:join rejected"));
    });
  });
}

/**
 * Connect to the per-job room and forward events to the caller.
 * Returns an unsubscribe fn that leaves the room (the underlying socket
 * stays open — multiple popups can share it). Rejects when the socket
 * cannot connect or join within CONNECT_TIMEOUT_MS.
 *
 * @param {{ jobId: string, onEvent: (event: any) => void }} opts
 * @returns {Promise<() => void>}
 */
export async function connectVideoExportSocket({ jobId, onEvent }) {
  const socket = getSocket();
  await waitForConnect(socket);
  await joinRoom(socket, jobId);
  const handler = (event) => {
    if (!event || event.jobId !== jobId) return;
    onEvent(event);
  };
  // Room membership is per connection — without re-joining after a
  // reconnect the popup silently stops receiving events.
  const rejoin = () => socket.emit("video-export:join", { jobId }, () => {});
  socket.on("video-export:event", handler);
  socket.on("connect", rejoin);
  return () => {
    socket.off("video-export:event", handler);
    socket.off("connect", rejoin);
    socket.emit("video-export:leave", { jobId }, () => {});
  };
}
